import React, { useState, useEffect } from 'react';
import { Box, Card, CardContent, Grid, Typography, Chip, TextField, Button } from '@mui/material';
import DataTable from '../../../sharedModules/dataTable';
import axios from '../../../sharedModules/utils/axios.utils';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

const vendorColumns = [
  {
    name: 'Location',
    selector: (row: any) => row.location,
    sortable: true,
    width: '120px',
  },
  {
    name: 'Vendor ID',
    selector: (row: any) => row.vendor_id,
    sortable: true,
    width: '120px',
  },
  {
    name: 'Vendor Name',
    selector: (row: any) => row.vendor_name,
    sortable: true,
    grow: 2,
  },
  {
    name: 'Account #',
    selector: (row: any) => row.acct_no || 'N/A',
    sortable: true,
    width: '120px',
  },
  {
    name: 'Type',
    selector: (row: any) => row.vendor_type || 'N/A',
    sortable: true,
    width: '100px',
  },
  {
    name: 'City',
    selector: (row: any) => row.city || 'N/A',
    sortable: true,
  },
  {
    name: 'State',
    selector: (row: any) => row.state || 'N/A',
    sortable: true,
    width: '80px',
  },
  {
    name: 'Phone',
    selector: (row: any) => row.phone || 'N/A',
    sortable: true,
  },
  {
    name: 'Status',
    selector: (row: any) => row.inactive,
    sortable: true,
    cell: (row: any) => (
      <Chip label={row.inactive ? 'Inactive' : 'Active'} color={row.inactive ? 'default' : 'success'} size="small" />
    ),
    width: '100px',
  },
  {
    name: 'Synced At',
    selector: (row: any) => (row.synced_at ? new Date(row.synced_at).toLocaleString() : 'N/A'),
    sortable: true,
    width: '180px',
  },
];

const ServmanVendorsListPage: React.FC = () => {
  const [vendors, setVendors] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const fetchVendors = async (search = '') => {
    try {
      setLoading(true);
      setError(null);
      const params: any = {};
      if (search) params.search = search;

      const response = await axios.get<string, any>('/vendors/servman_vendors', { params });
      setVendors(response?.data || []);
    } catch (error) {
      console.error('Error fetching ServMan vendors:', error);
      setError('Failed to load ServMan vendors. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVendors();
  }, []);

  const handleSearch = () => {
    fetchVendors(searchTerm);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%' }}>
        <Grid container>
          <Grid item xs={12} style={{ marginTop: 20 }}>
            <Typography style={{ fontSize: '20px' }} variant="h3">
              Vendor Management <NavigateNextIcon fontSize="small" style={{ margin: '-4px 4px' }} /> ServMan Vendors
            </Typography>
          </Grid>
          <Grid item xs={12} style={{ marginTop: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>Loading vendors...</div>
          </Grid>
        </Grid>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ width: '100%' }}>
        <Grid container>
          <Grid item xs={12} style={{ marginTop: 20 }}>
            <Typography style={{ fontSize: '20px' }} variant="h3">
              Vendor Management <NavigateNextIcon fontSize="small" style={{ margin: '-4px 4px' }} /> ServMan Vendors
            </Typography>
          </Grid>
          <Grid item xs={12} style={{ marginTop: 20 }}>
            <div style={{ color: 'red', textAlign: 'center', padding: '50px' }}>{error}</div>
          </Grid>
        </Grid>
      </Box>
    );
  }

  return (
    <Grid container>
      <Grid item xs={12} style={{ marginTop: 20 }}>
        <Grid container justifyContent="space-between">
          <Grid item style={{ marginBottom: 3 }}>
            <Typography style={{ display: 'inline-block', fontSize: '20px' }} variant="h3">
              Vendor Management
            </Typography>
            <NavigateNextIcon
              fontSize="small"
              style={{
                color: '#3d5a80 !important',
                fontWeight: 700,
                fontSize: '20px !important',
                margin: '-4px 4px',
              }}
            />
            <Typography style={{ display: 'inline-block', fontSize: '20px' }} variant="h3">
              ServMan Vendors
            </Typography>
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12} style={{ marginTop: 20 }}>
        <Card>
          <CardContent>
            <Grid container spacing={2} alignItems="flex-start" onKeyDown={handleKeyPress} style={{ marginBottom: 16 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Search by Vendor Name"
                  variant="standard"
                  style={{ width: '100%', marginTop: 8 }}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Enter vendor name"
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Button onClick={handleSearch} variant="contained" style={{ marginTop: 20 }}>
                  Search
                </Button>
              </Grid>
            </Grid>

            <Box sx={{ mt: 3 }}>
              <DataTable
                title=""
                columns={vendorColumns}
                data={vendors}
                pagination
                paginationPerPage={25}
                paginationRowsPerPageOptions={[10, 25, 50, 100]}
                highlightOnHover
                pointerOnHover
                responsive
                dense
                noHeader
              />
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default ServmanVendorsListPage;
